import { Component, ViewChild, OnInit } from '@angular/core';
import { ApiService } from '@app/services/api.service';
import { throwError } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { EudsDataTableComponent, EudsPaginationComponent } from '@eucoms/common-components/design-system';
import { AppConfigService } from '@eucoms/common-components';
import { environment } from '@environments/environment';

interface LogDataRow {
  timestamp: string;
  itemType: string;
  combinedMessage: string;
  cloud_RoleName: string;
}

// An interface for the App Insights API response structure
interface AppInsightsQueryResult {
  tables: {
    name: string;
    columns: { name: string; type: string }[];
    rows: any[][]; // Rows are arrays of values
  }[];
}

@Component({
  selector: 'app-logging',
  templateUrl: './logging.component.html',
  styleUrls: ['./logging.component.scss']
})
export class LogComponent implements OnInit {

  opCoSelected: string | null = null;
  isProduction: boolean = false; 
  isDevEnvironment: boolean = false;

  // The data table
  @ViewChild('tableLogs') tableLogs: EudsDataTableComponent;

  // The child pagination component
  @ViewChild('paginationLogs') paginationLogs: EudsPaginationComponent;

  // Data table column definitions - MUST match the 'project' order in KQL
  columnDefsLog = [
    {
      'dataField': 'timestamp',
      'header': 'Timestamp',
      'sortable': true
    },
    {
      'dataField': 'itemType',
      'header': 'Item Type',
      'sortable': true
    },
    {
      'dataField': 'combinedMessage',
      'header': 'Message',
      'sortable': false // Usually not useful to sort by long messages
    },
    {
      'dataField': 'cloud_RoleName',
      'header': 'Cloud Role',
      'sortable': true
    }
  ];

  originalLogData: LogDataRow[] = [];
  fetchedLogData: LogDataRow[] = []; // Holds all fetched data   
  displayedLogData: LogDataRow[] = []; // Holds data for the current page

  itemsPerPageOptions: Array<number> = [10, 25, 50];
  itemsPerPage: number = 10;
  totalLogs: number = 0;
  currentPage: number = 1;

  isLoading: boolean = false;
  error: string | null = null;
  searchTerm: string = '';
  environment: string = '';

  // Search text filter
  searchText: string = '';
  filterCount: number = 0;
  resultMessageText: string = '';

  constructor(
    private appConfig: AppConfigService,
    private apiService: ApiService
  ) {
  }

  ngOnInit(): void {
    this.opCoSelected ??= this.appConfig.config.opCo;
    this.isProduction = environment.production;
    this.isDevEnvironment = environment.devEnvironment;
    const env = (this.appConfig.config.custtpApiUrl.match(/(?<=-)(?i:dev|stage|test)/))
    this.environment = this.isDevEnvironment ? 'dev' : this.isProduction ? 'prod' : env;

    if (!this.fetchedLogData.length && !this.isLoading) {
       this.fetchLogData();
    }
  }

  fetchLogData(): void {

    this.isLoading = true;
    this.error = null;
    this.originalLogData = [];
    this.fetchedLogData = [];
    this.displayedLogData = [];
    this.totalLogs = 0;
    this.currentPage = 1;
    this.apiService.queryAppInsightsLogs(this.searchTerm, this.opCoSelected, this.environment)
      .pipe(
        finalize(() => {
          this.isLoading = false; // Ensure loading is set to false when done
          console.log('LogComponent: Fetch complete.');
        }),
        catchError(err => {
          console.error('LogComponent: Error fetching logs:', err);
          this.error = `Failed to load logs: ${err.message || 'Unknown error'}`;
          this.originalLogData = [];
          this.fetchedLogData = []; // Clear data on error
          this.displayedLogData = [];
          this.totalLogs = 0;
          this.filterCount = 0;
          return throwError(() => err); // Re-throw the error
        })
      )
      .subscribe((response: AppInsightsQueryResult) => {
        console.log('LogComponent: Received API response:', response);
        if (response && response.tables && response.tables.length > 0 && response.tables[0].rows) {
          const table = response.tables[0];
          // Map column names to their index for reliable object creation
          const columnMap: { [key: string]: number } = {};
          table.columns.forEach((col, index) => {
            columnMap[col.name] = index;
          });

          // Transform rows (arrays) into objects based on column map
          let transformedData = table.rows.map(row => ({
            timestamp: row[columnMap['timestamp']],
            itemType: row[columnMap['itemType']],
            combinedMessage: row[columnMap['combinedMessage']],
            cloud_RoleName: row[columnMap['cloud_RoleName']]
          }));
          
          this.fetchedLogData = transformedData;
          this.originalLogData = [...transformedData];
          console.log('LogComponent: Transformed data:', this.fetchedLogData);
          this.totalLogs = this.fetchedLogData.length;
          this.filterCount = this.totalLogs;
          this.showPageItemsForPagination(1); // Show the first page
        } else {
          console.warn('LogComponent: No data found in the API response or unexpected structure.');
          this.originalLogData = [];
          this.fetchedLogData = [];
          this.displayedLogData = [];
          this.totalLogs = 0;
          this.filterCount = 0;
        }
      });
  }

  applySearch(): void {
    this.currentPage = 1; // Reset to first page when applying search
    this.fetchLogData();
  }

  // Function to sort the data
  public sortTableData(event: any) {
    const { field: sortField, order: sortOrder } = event;
    if (sortOrder !== 'none' && this.fetchedLogData) {
       // Create a copy before sorting to avoid modifying the original fetched array directly if needed elsewhere
       const dataToSort = [...this.fetchedLogData];
       dataToSort.sort((a: LogDataRow, b: LogDataRow) => {
          // Handle potential null/undefined values during sort
          const valA = a[sortField as keyof LogDataRow] ?? '';
          const valB = b[sortField as keyof LogDataRow] ?? '';
          // Basic string/number comparison
          if (valA > valB) return sortOrder === 'ascending' ? 1 : -1;
          if (valA < valB) return sortOrder === 'ascending' ? -1 : 1;
          return 0;
       });
       this.fetchedLogData = dataToSort; // Update fetched data with sorted order
    }
    // Update column definitions to reflect sort order in UI
    this.columnDefsLog.forEach((col: any) => {
       col.sortOrder = col.dataField === sortField ? sortOrder : 'none';
    });
    // Refresh the displayed data for the current page
    this.showPageItemsForPagination(this.currentPage);
 }

  // Function to set the data shown based on the page chosen
  public onChangePage(page: number = 1): void {
    this.currentPage = page;
    this.showPageItemsForPagination(page);
  }

  // Function to reset the data shown when the items to show per page has been changed
  public onChangeItemsPerPage(items: number): void {
    this.itemsPerPage = items;
    this.currentPage = 1; // Reset to first page when items per page changes
    this.showPageItemsForPagination(this.currentPage);
  }

  // Function to show a subset of the data based on the current page
  private showPageItemsForPagination(page: number = 1): void {
    if (!this.fetchedLogData) {
      this.displayedLogData = [];
      return;
    }
    const startItem = (page - 1) * this.itemsPerPage;
    const endItem = page * this.itemsPerPage;
    this.totalLogs = this.fetchedLogData.length; // Recalculate total based on potentially filtered data
    // Ensure pagination component's totalItems is updated if it relies on input binding
    if (this.paginationLogs) {
        this.paginationLogs.totalItems = this.totalLogs;
    }

    this.displayedLogData = this.fetchedLogData.slice(startItem, endItem);
    console.log(`LogComponent: Displaying page ${page}, items ${startItem + 1}-${Math.min(endItem, this.totalLogs)} of ${this.totalLogs}`);
  }

  clearSearch(): void {
    this.searchTerm = '';
    this.applySearch();
  }

  filterDataSource(event: any): void {
    this.searchText = event;
    let tableData = [...this.originalLogData];

    if (event) {
      const search = this.searchText.toLowerCase();
      tableData = tableData.filter(logEntry => {
        return (logEntry.itemType as string)?.toLowerCase().includes(search)
          || (logEntry.combinedMessage as string)?.toLowerCase().includes(search)
          || (logEntry.cloud_RoleName as string)?.toLowerCase().includes(search);
      });
    }

    this.filterCount = tableData.length;
    this.resultMessageText = `${this.filterCount} log entries found`;

    this.fetchedLogData = tableData;    
    this.totalLogs = tableData.length;

    this.currentPage = 1;
    if (this.paginationLogs) {
      this.paginationLogs.currentPage = 1;
    }

    this.showPageItemsForPagination(this.currentPage);
  }

  resetDataSource(event: any): void {
    this.searchText = '';

    this.displayedLogData = [...this.originalLogData];
    this.totalLogs = this.originalLogData.length;
    this.filterCount = this.totalLogs;
    this.resultMessageText = `${this.filterCount} log entries found.`;

    this.currentPage = 1;
    if (this.paginationLogs) {
      this.paginationLogs.currentPage = 1;
    }

    this.showPageItemsForPagination(this.currentPage);
  }
}
